import { copyToClipboard } from '../../support/clipboard';

const COPIED_MESSAGE_MS = 1600;
const COPIED_HINT_MESSAGE_MS = 2500;

/**
 * Share / QR modals for a provider profile. The open state lives on the Livewire component
 * (`isQrModalOpen`, `isShareModalOpen`) so the modals survive re-renders.
 *
 * @param {{ profileUrl?: string, shareText?: string }} config
 */
export default function providerProfileShare({ profileUrl = '', shareText = '' } = {}) {
    const message = `${shareText}\n${profileUrl}`;

    const shareLinks = {
        whatsapp: `https://wa.me/?text=${encodeURIComponent(message)}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(profileUrl)}`,
        twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`,
        telegram: `https://t.me/share/url?url=${encodeURIComponent(profileUrl)}&text=${encodeURIComponent(shareText)}`,
    };

    return {
        profileUrl,
        shareText,
        copiedLabel: '',

        openQrModal() {
            this.copiedLabel = '';
            this.$wire.set('isQrModalOpen', true);
        },

        closeQrModal() {
            this.$wire.set('isQrModalOpen', false);
        },

        openShareModal() {
            this.copiedLabel = '';
            this.$wire.set('isQrModalOpen', false);
            this.$wire.set('isShareModalOpen', true);
        },

        closeShareModal() {
            this.$wire.set('isShareModalOpen', false);
        },

        closeAllModals() {
            this.$wire.set('isQrModalOpen', false);
            this.$wire.set('isShareModalOpen', false);
        },

        async copyProfileUrl() {
            const copied = await copyToClipboard(this.profileUrl);

            this.flashLabel(copied ? 'تم نسخ الرابط' : 'تعذر نسخ الرابط', copied ? COPIED_MESSAGE_MS : 0);
        },

        async openPlatformShare(platform) {
            if (platform === 'instagram' || platform === 'snapchat') {
                await this.shareWithNativeSheet();

                return;
            }

            if (shareLinks[platform]) {
                window.open(shareLinks[platform], '_blank', 'noopener,noreferrer');
            }
        },

        /** Uses the device share sheet, or copies the link for platforms without a web share URL. */
        async shareWithNativeSheet() {
            if (navigator.share) {
                try {
                    await navigator.share({ title: this.shareText, url: this.profileUrl });

                    return;
                } catch (error) {
                    if (error.name === 'AbortError') {
                        return;
                    }
                }
            }

            const copied = await copyToClipboard(this.profileUrl);

            this.flashLabel(
                copied ? 'تم نسخ الرابط، افتحه في التطبيق' : 'تعذر نسخ الرابط',
                copied ? COPIED_HINT_MESSAGE_MS : 0
            );
        },

        /** Shows a message, clearing it after `duration` ms (0 keeps it until the next action). */
        flashLabel(text, duration) {
            this.copiedLabel = text;

            if (duration > 0) {
                setTimeout(() => (this.copiedLabel = ''), duration);
            }
        },
    };
}
